import { useMemo, useState } from 'react'
import { useAuth } from '../config/context/auth'
import { useTranslation } from 'react-i18next'
import { useAsync } from 'react-use'
import { getTeamAccessSummary } from '../api/team'
import capitalize from 'lodash/capitalize'
import Section from './Section'
import { DataTable } from '@/components/ui/data-table'
import { ColumnDef } from '@tanstack/react-table'
import { usePageInfo } from '../config/queries'
import LoginButtons from './LoginButtons'
import TroubleshootingModal from '../pages/UserDashboard/components/TroubleshootingModal'
import { Button, buttonVariants } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { refreshPersonalCharacters } from '../api/user'
import dayjs from 'dayjs'
import { updateGuild } from '../api/guild'
import { useGuild } from '../config/context/guild'
import Card from './Card'
import LinkButton from '@/components/ui/link-button'
import { useRefreshData } from '../hooks/useRefreshData'
import { hasAccessToContent } from '../config/permissions'
import LeaveTeamButton from './LeaveTeamButton'
import { useGameVersion } from '../hooks/useGameVersion'

const GUILD_MASTER_ADMIN_RESTRICTION: wowaudit.AccessRestriction = {
  id: -1,
  accessLevel: 'admin',
  defaultRestriction: true,
  gameRankThreshold: '0',
  restrictionType: 'game_rank',
  section: 'base',
}

const UnauthorizedInfo = ({
  teamReference,
}: {
  teamReference?: wowaudit.TeamReference
}) => {
  const { t } = useTranslation()
  const {
    data: { user },
  } = usePageInfo()
  const { guild, guildReference } = useGuild()
  const { accessLevel, section, requirement } = useAuth()
  const gameVersion = useGameVersion()
  const trustsGuildRanks = gameVersion.has('blizzard_api')
  const [helpModalOpen, setHelpModalOpen] = useState(false)
  const { value: accessDetails } = useAsync(async () => {
    return teamReference
      ? await getTeamAccessSummary(teamReference.id)
      : undefined
  }, [teamReference])

  const tokenExpired = user && dayjs(user.oauthExpiresAt).isBefore(dayjs())
  const { refreshing, refreshData } = useRefreshData({
    retriever: refreshPersonalCharacters,
    queryToInvalidate: ['pageInfo'],
    callback: () => window.location.reload(),
  })

  const { refreshing: refreshingGuild, refreshData: refreshGuild } =
    useRefreshData({
      retriever: updateGuild,
      id: guildReference?.id,
      callback: () => refreshData(),
    })

  const relevantRestrictions = useMemo(() => {
    if (!accessDetails) return undefined

    const grantsAccess = (r: wowaudit.AccessRestriction) =>
      requirement === r.accessLevel || r.accessLevel === 'admin'

    const sectionRestrictions = accessDetails.accessRestrictions.filter(
      (r) => r.restrictionType !== 'user' && r.section === section,
    )

    let restrictions: wowaudit.AccessRestriction[]

    if (sectionRestrictions.length > 0 && section !== 'base') {
      const baseAdminRestrictions = accessDetails.accessRestrictions.filter(
        (r) =>
          r.restrictionType !== 'user' &&
          r.section === 'base' &&
          r.accessLevel === 'admin',
      )

      restrictions = [
        ...sectionRestrictions.filter(grantsAccess),
        ...baseAdminRestrictions,
      ]
    } else if (sectionRestrictions.length > 0) {
      restrictions = sectionRestrictions.filter(grantsAccess)
    } else {
      restrictions = accessDetails.accessRestrictions.filter(
        (r) =>
          r.restrictionType !== 'user' &&
          r.section === 'base' &&
          grantsAccess(r),
      )
    }

    const hasGameRankBaseAdmin = accessDetails.accessRestrictions.some(
      (r) =>
        r.restrictionType === 'game_rank' &&
        r.section === 'base' &&
        r.accessLevel === 'admin',
    )

    if (trustsGuildRanks && !hasGameRankBaseAdmin) {
      restrictions = [...restrictions, GUILD_MASTER_ADMIN_RESTRICTION]
    }

    return restrictions
  }, [accessDetails, section, requirement, trustsGuildRanks])

  const columns: ColumnDef<wowaudit.AccessRestriction>[] = useMemo(
    () => [
      {
        accessorKey: 'restrictionType',
        header: 'Access by',
        cell: (info) => {
          const restrictionType = info.getValue() as string
          return capitalize(restrictionType.replace('_', ' '))
        },
      },
      {
        id: 'identifier',
        header: 'Required rank',
        cell: (info) => {
          if (info.row.original.restrictionType === 'game_rank') {
            return Number(info.row.original.gameRankThreshold) === 0
              ? t('settings.access.ranks.0')
              : Number(info.row.original.gameRankThreshold) + 1
          } else if (info.row.original.restrictionType === 'team_rank') {
            return (
              accessDetails?.teamRanks.find(
                (r) => r.id === info.row.original.teamRankId,
              )?.name ?? ''
            )
          }
          return '-'
        },
      },
      {
        accessorKey: 'accessLevel',
        header: 'Level',
        cell: (info) => {
          return capitalize(info.getValue() as string)
        },
      },
      {
        id: 'userStatus',
        header: 'Your rank',
        cell: (info) => {
          if (info.row.original.restrictionType === 'game_rank') {
            return (
              <span className='text-destructive-foreground'>
                {accessDetails?.bestRank != null
                  ? accessDetails.bestRank + 1
                  : 'not in guild'}
              </span>
            )
          } else if (info.row.original.restrictionType === 'team_rank') {
            return (
              <span className='text-destructive-foreground'>
                {(accessDetails?.teamRanksForCurrentUser ?? [])
                  .map(
                    (rank) =>
                      accessDetails?.teamRanks.find((r) => r.id === rank.id)
                        ?.name ?? '',
                  )
                  .join(', ') || 'not in team'}
              </span>
            )
          }
          return '-'
        },
      },
    ],
    [accessDetails, t],
  )

  const canLeaveTeam =
    !!teamReference &&
    !hasAccessToContent('member', 'base', guild, teamReference).hasAccess
  const leaveTeamButton =
    canLeaveTeam && teamReference ? (
      <LeaveTeamButton
        teamId={teamReference.id}
        teamPath={teamReference.path}
        variant='outline'
        size='default'
      />
    ) : null

  return (
    <Section title={t('errors.unauthorized')} className='text-center mt-16'>
      <h3>
        {user ? t('errors.unauthorized') : t('shared.log-in-to-continue')}
      </h3>
      <p>
        <strong>{capitalize(requirement)}</strong> level
        {section === 'base' ? ' base' : ' '} access
        {section === 'base' ? ' ' : ` to the ${section} section`} is required to
        view this team page, but you currently have{' '}
        <strong>{accessLevel === 'none' ? 'no' : accessLevel}</strong> access.
        {user
          ? user.characterReferences.length > 0
            ? ' Meeting one of the following rules would allow you to access this section:'
            : ''
          : ' Have you tried logging in?'}
      </p>

      {!user ? (
        <Card className='my-6 py-3 px-4 inline-flex'>
          <LoginButtons />
        </Card>
      ) : null}

      {user && user.characterReferences.length === 0 ? (
        <Card className='my-6 p-5 inline-flex'>
          <div>
            <p>{t('errors.no_user_characters_found')}</p>

            <div className='flex gap-2 justify-center mt-3'>
              {guildReference?.path ? (
                <LinkButton to={guildReference?.path}>View guild</LinkButton>
              ) : null}

              {tokenExpired ? (
                <a
                  href={`/auth/bnet`}
                  className={`${buttonVariants({ variant: 'outline' })}`}
                >
                  Re-authenticate
                </a>
              ) : (
                <Button
                  variant='outline'
                  onClick={() => refreshData()}
                  loading={refreshing}
                  spinner='blizzard'
                >
                  Fetch characters
                </Button>
              )}

              <Button variant='outline' onClick={() => setHelpModalOpen(true)}>
                Help
              </Button>
            </div>
          </div>
        </Card>
      ) : null}

      {helpModalOpen && (
        <TroubleshootingModal
          inProfile={false}
          setOpen={(value: boolean) => setHelpModalOpen(value)}
        />
      )}

      {user &&
      user.characterReferences.length > 0 &&
      relevantRestrictions?.length ? (
        <div className='flex flex-col gap-6 items-center justify-center mt-6 text-left'>
          <DataTable
            columns={columns}
            data={relevantRestrictions}
            includeHeader={true}
          />

          <div className='flex gap-2 justify-center'>
            {leaveTeamButton}

            {guildReference?.path ? (
              <LinkButton to={guildReference?.path}>View guild</LinkButton>
            ) : null}

            {tokenExpired ? (
              <a
                className={`${buttonVariants({ variant: 'outline' })}`}
                href={`/auth/bnet`}
              >
                Re-authenticate
              </a>
            ) : (
              <Button
                variant='outline'
                onClick={() => refreshGuild()}
                loading={refreshing || refreshingGuild}
                spinner='blizzard'
              >
                Sync access
              </Button>
            )}

            <Button variant='outline' onClick={() => setHelpModalOpen(true)}>
              Help
            </Button>
          </div>

          {trustsGuildRanks &&
            relevantRestrictions?.some(
              (r) => r.restrictionType === 'game_rank',
            ) && (
              <Alert className='max-w-176 text-center'>
                <AlertDescription>
                  If you recently joined this guild or changed ranks, it can
                  take up to 2 days for Blizzard&apos;s API to update.
                </AlertDescription>
              </Alert>
            )}
        </div>
      ) : null}

      {leaveTeamButton && !relevantRestrictions?.length ? (
        <div className='flex gap-2 justify-center mt-6'>{leaveTeamButton}</div>
      ) : null}
    </Section>
  )
}

export default UnauthorizedInfo
